import { n as __reExport, t as __exportAll } from "./chunk-rolldown-runtime.mjs";
import { t as storage_exports } from "./chunk-storage.mjs";
import { log } from "@dxos/log";
import * as Effect from "effect/Effect";
import { SubscriptionList } from "@dxos/async";
import * as Array from "effect/Array";
import * as Function from "effect/Function";
import { invariant } from "@dxos/invariant";
export * from "#storage";
//#region src/Observability.ts
var Observability_exports = /* @__PURE__ */ __exportAll({
	addDataProvider: () => addDataProvider,
	addExtension: () => addExtension,
	initialize: () => initialize,
	make: () => make
});
__reExport(Observability_exports, storage_exports);
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/observability/src/Observability.ts";
var attachAiCapture = async (observability) => {
	const { AiSpanProcessor, contentCaptureAllowed } = await import("./ai/AiObservability.mjs");
	const { Otel } = await import("./chunk-extensions.mjs").then((n) => n.t);
	return Otel.addSpanProcessor(new AiSpanProcessor({
		captureInference: (inference) => observability.ai.captureInference(inference),
		captureTurn: (turn) => observability.ai.captureTurn(turn),
		captureToolCall: (toolCall) => observability.ai.captureToolCall(toolCall),
		captureEnabled: () => observability.enabled,
		allowContent: contentCaptureAllowed
	}));
};
var ObservabilityImpl = class {
	_initialized = false;
	_extensions = [];
	_dataProviders = [];
	_subscriptions = new SubscriptionList();
	initialize() {
		if (this._initialized) return Effect.succeed(void 0);
		const initializedExtensions = [];
		return Effect.gen({ self: this }, function* () {
			const context = { setTags: (tags, kind) => this.setTags(tags, kind) };
			for (const extension of this._extensions) {
				if (extension.initialize) yield* extension.initialize(context);
				initializedExtensions.push(extension);
			}
			const cleanups = yield* Effect.all(this._dataProviders.map((provider) => provider(this)));
			this._subscriptions.add(...cleanups.filter((cleanup) => cleanup !== void 0));
			this._subscriptions.add(yield* Effect.promise(() => attachAiCapture(this)));
			this._initialized = true;
		}).pipe(Effect.catch((error) => Effect.gen({ self: this }, function* () {
			log.catch(error, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 102,
				S: this
			});
			for (const extension of initializedExtensions) if (extension.close) yield* extension.close().pipe(Effect.catch(() => Effect.succeed(void 0)));
			this._subscriptions.clear();
		})));
	}
	close() {
		return Effect.gen({ self: this }, function* () {
			this._subscriptions.clear();
			this._dataProviders.length = 0;
			for (const extension of this._extensions) if (extension.close) yield* extension.close();
			this._initialized = false;
		});
	}
	enable() {
		return Effect.gen({ self: this }, function* () {
			for (const extension of this._extensions) if (extension.enable) yield* extension.enable();
		});
	}
	disable() {
		return Effect.gen({ self: this }, function* () {
			for (const extension of this._extensions) if (extension.disable) yield* extension.disable();
		});
	}
	flush() {
		return Effect.gen({ self: this }, function* () {
			for (const extension of this._extensions) if (extension.flush) yield* extension.flush();
		});
	}
	_addExtension(extension) {
		invariant(!this._initialized, "Observability is already initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 159,
			S: this,
			A: ["!this._initialized", "'Observability is already initialized'"]
		});
		this._extensions.push(extension);
	}
	_addDataProvider(dataProvider) {
		invariant(!this._initialized, "Observability is already initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 164,
			S: this,
			A: ["!this._initialized", "'Observability is already initialized'"]
		});
		this._dataProviders.push(dataProvider);
	}
	/**
	* Adds a data provider and initializes it.
	*/
	addDataProvider(dataProvider) {
		return Effect.gen({ self: this }, function* () {
			this._dataProviders.push(dataProvider);
			const cleanup = yield* dataProvider(this);
			if (cleanup) this._subscriptions.add(cleanup);
		});
	}
	identify(distinctId, attributes, setOnceAttributes) {
		for (const extension of this._extensions) extension.identify?.(distinctId, attributes, setOnceAttributes);
	}
	alias(distinctId, previousId) {
		for (const extension of this._extensions) extension.alias?.(distinctId, previousId);
	}
	setTags(tags, kind) {
		for (const extension of this._extensions) {
			if (kind && !extension.apis.some((api) => api.kind === kind)) continue;
			const processedTags = Object.fromEntries(Object.entries(tags).filter((entry) => entry[1] !== void 0).map(([key, value]) => [key, value.toString()]));
			extension.setTags?.(processedTags, kind);
		}
	}
	get enabled() {
		return this._extensions.every((extension) => extension.enabled);
	}
	get errors() {
		return { captureException: (error, attributes) => {
			for (const extension of this._getExtensions("errors")) extension.captureException(error, attributes);
		} };
	}
	get events() {
		return { captureEvent: (event, attributes) => {
			for (const extension of this._getExtensions("events")) extension.captureEvent(event, attributes);
		} };
	}
	get mcp() {
		return {
			captureInitialize: (client) => {
				for (const extension of this._getExtensions("mcp")) extension.captureInitialize(client);
			},
			captureToolCall: (call) => {
				for (const extension of this._getExtensions("mcp")) extension.captureToolCall(call);
			}
		};
	}
	get ai() {
		return {
			captureInference: (inference) => {
				for (const extension of this._getExtensions("ai")) extension.captureInference(inference);
			},
			captureTurn: (turn) => {
				for (const extension of this._getExtensions("ai")) extension.captureTurn(turn);
			},
			captureToolCall: (toolCall) => {
				for (const extension of this._getExtensions("ai")) extension.captureToolCall(toolCall);
			}
		};
	}
	get support() {
		return {
			uploadLogs: async () => {
				let key;
				for (const extension of this._getExtensions("support")) key = await extension.uploadLogs() ?? key;
				return key;
			},
			uploadNdjson: async (ndjson, kind) => {
				let key;
				for (const extension of this._getExtensions("support")) key = await extension.uploadNdjson(ndjson, kind) ?? key;
				return key;
			},
			sessionContext: () => {
				for (const extension of this._getExtensions("support")) {
					const context = extension.sessionContext();
					if (context) return context;
				}
			},
			flushLogs: async (attributes) => {
				for (const extension of this._getExtensions("support")) await extension.flushLogs(attributes);
			}
		};
	}
	isAvailable(kind) {
		const apis = this._getExtensions(kind);
		if (apis.length === 0) return Effect.succeed(false);
		return Effect.gen({ self: this }, function* () {
			for (const api of apis) if (yield* api.isAvailable()) return true;
			return false;
		});
	}
	get metrics() {
		return {
			gauge: (name, value, attributes, meta) => {
				for (const extension of this._getExtensions("metrics")) extension.gauge(name, value, attributes, meta);
			},
			increment: (name, value, attributes, meta) => {
				for (const extension of this._getExtensions("metrics")) extension.increment(name, value, attributes, meta);
			},
			distribution: (name, value, attributes, meta) => {
				for (const extension of this._getExtensions("metrics")) extension.distribution(name, value, attributes, meta);
			},
			observe: (name, callback, attributes, meta) => {
				const cleanups = this._getExtensions("metrics").map((extension) => extension.observe(name, callback, attributes, meta));
				return () => {
					for (const cleanup of cleanups) cleanup();
				};
			}
		};
	}
	_getExtensions(kind) {
		return Function.pipe(this._extensions, Array.flatMap((extension) => extension.apis), Array.filter((api) => api.kind === kind));
	}
};
var make = () => Effect.succeed(new ObservabilityImpl());
var addExtension = (_extension) => Effect.fn(function* (_observability) {
	const observability = yield* _observability;
	const extension = yield* _extension;
	invariant("_addExtension" in observability && typeof observability._addExtension === "function", void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 367,
		S: this,
		A: ["'_addExtension' in observability && typeof observability._addExtension === 'function'", ""]
	});
	observability._addExtension(extension);
	return observability;
});
var addDataProvider = (dataProvider) => Effect.fn(function* (_observability) {
	const observability = yield* _observability;
	invariant("_addDataProvider" in observability && typeof observability._addDataProvider === "function", void 0, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file,
		L: 375,
		S: this,
		A: ["'_addDataProvider' in observability && typeof observability._addDataProvider === 'function'", ""]
	});
	observability._addDataProvider(dataProvider);
	return observability;
});
var initialize = Effect.fn(function* (_observability) {
	const observability = yield* _observability;
	yield* observability.initialize();
	return observability;
});
//#endregion
export { addDataProvider, addExtension, initialize, make, Observability_exports as t };

//# sourceMappingURL=Observability.mjs.map